import React, { useState, useEffect, useRef } from "react";
import AddButton from "./AddButton";
import SequenceableControls from "./SequenceableControls";
import DrEditor from "./Editor";
import Spinner from "../../Spinner";

/**
 * <RichTextList syllabusId={} api={} type={} plType={} listTitle={} typeLookupId={optional} editMode={} reqOptions={} focus={} focusKey={} lastUpdateType={} defaultText={} containerId={} addItem={} />
 */
const StatefulRichTextList = (props) => {
  try {
    const _isMounted = useRef(false);
    const {
      syllabusId,
      progAIndex,
      api,
      editMode,
      type,
      plType,
      reqOptions,
      lastUpdateType,
      updateTime,
      typeLookupId,
      listTitle,
      unitId,
      parentType,
      parentId,
    } = props;
    const url = `${api}${plType}`;
    const [items, setItems] = useState(props.items || []);
    const [maxSequence, setMaxSequence] = useState(items.length);
    const [dataReady, setDataReady] = useState(!!props.items);
    const [reload, setReload] = useState(!props.items);

    /** Naming event hook functions makes profiling easier **/
    useEffect(function updateMounted() {
      _isMounted.current = true;
      return function cleanupMounted() {
        _isMounted.current = false;
      };
    }, []);

    useEffect(
      function itemsPropUpdate() {
        if (props.items) {
          setItems(props.items);
          setMaxSequence(props.items.length);
        }
      },
      [props.items]
    );

    useEffect(
      function fetchItems() {
        if (reload) {
          apiCall(
            `${url}?${type}[syllabus_id]=${syllabusId}&${type}[type_lookup_id]=${typeLookupId}&${type}[unit_id]=${unitId}&${type}[${parentType}_id]=${parentId}`,
            reqOptions("GET"),
            (res) => {
              res.json().then((data) => {
                if (_isMounted.current) {
                  setItems(data);
                  setMaxSequence(data.length);
                  setDataReady(true);
                  setReload(false);
                }
              });
            }
          );
        }
      },
      [reload]
    );

    useEffect(
      function reloadEffect() {
        if (
          (lastUpdateType === type ||
            lastUpdateType === `${type}-${typeLookupId}`) &&
          _isMounted.current
        ) {
          setReload(true);
        }
      },
      [updateTime]
    );

    const addItem = (opts, e) => {
      console.log("ADD AN ITEM?");
      const body = {};
      body[type] = {
        syllabus_id: syllabusId,
        unit_id: unitId,
        sequence: 1,
        type_lookup_id: typeLookupId,
      };
      body[type][`${parentType}_id`] = parentId;
      apiCall(url, reqOptions("POST", body), (res) => setReload(true));
    };

    const removeItem = (opts, e) => {
      if (confirm("Are you sure you want to remove this item?")) {
        const { id } = opts;
        const body = {};
        body[type] = { syllabus_id: syllabusId, active: false };
        apiCall(`${url}/${id}`, reqOptions("PATCH", body), (res) =>
          setReload(true)
        );
      }
    };

    const updateItemSequence = (opts, e) => {
      console.log("UPDATE SEQ");
      const { id, index, value } = opts;
      const body = {};
      body[type] = { syllabus_id: syllabusId, sequence: value };
      apiCall(`${url}/${id}`, reqOptions("PATCH", body), (res) =>
        setReload(true)
      );
    };

    const listHeader = listTitle ? (
      <>
        {listTitle}
        {editMode ? <AddButton addItem={addItem} /> : ""}
      </>
    ) : (
      ""
    );

    const display =
      items.length > 0 || editMode ? (
        <>
          <strong>{listHeader}</strong>
          <ul className={`sm-font ${listTitle ? "pl-4" : "pl-0"}`}>
            {items.map((i, ix) => (
              <StatefulRichText
                {...props}
                key={`${type}-${i.id}`}
                parentKey={`${type}-${i.id}`}
                item={i}
                description={i.description || ""}
                ix={ix}
                maxSequence={maxSequence}
                handleChange={updateItemSequence}
                removeItem={removeItem}
                dataReady={dataReady}
              />
            ))}
          </ul>
        </>
      ) : (
        ""
      );

    return dataReady ? display : <Spinner />;
  } catch (e) {
    console.error(e);
  }
};

/**
 * <StatefulRichText {...props} item={} maxSequence={} ix={} />
 */
const StatefulRichText = (props) => {
  try {
    const {
      api,
      item,
      syllabusId,
      type,
      plType,
      reqOptions,
      maxSequence,
      ix,
      defaultText,
      focusKey,
      dataReady,
      parentKey,
      containerId,
    } = props;
    const url = `${api}${plType}/${item.id}`;
    const [description, setDescription] = useState(props.description || "");
    const [timer, setTimer] = useState(null);
    const _isMounted = useRef(false);
    const _localUpdate = useRef(false);

    const updateDescription = (opts, e) => {
      if (description != e.target.value) {
        _localUpdate.current = true;
        setDescription(e.target.value);
      }
    };

    const debounce = (fn) => {
      if (timer) clearTimeout(timer);
      if (_isMounted.current) {
        setTimer(
          setTimeout(() => {
            fn();
          }, 300)
        );
      }
    };

    useEffect(() => {
      _isMounted.current = true;
      return () => {
        console.log("cleaup");
        _isMounted.current = false;
      };
    }, []);

    useEffect(() => {
      if (_localUpdate.current) {
        const body = { container_id: containerId };
        body[type] = { syllabus_id: syllabusId, description: description };
        debounce(() => {
          apiCall(url, reqOptions("PATCH", body));
        });
      }
    }, [description, props.description]);

    useEffect(() => {
      _localUpdate.current = false;
      setDescription(props.description);
    }, [props.description]);

    return (
      <li id={parentKey} className={`test-${type} list-unstyled`}>
        <div className="d-flex justify-content-between align-items-top">
          <div
            className="d-flex justify-content-between w-75 align-items-top"
            onClick={(e) => props.focus(parentKey, e)}
          >
            <span className="xs-font">{item.sequence}.</span>
            <DrEditor
              updateType={type}
              defaultText={defaultText}
              text={description}
              parentKey={parentKey}
              handleChange={updateDescription}
              focusKey={focusKey}
              dataReady={dataReady}
            />
          </div>
          <SequenceableControls
            {...props}
            itemIndex={ix}
            parentKey={parentKey}
            maxSequence={maxSequence}
          />
        </div>
      </li>
    );
  } catch (e) {
    console.error(e);
  }
};

const apiCall = (url, reqOptions, next) => {
  try {
    fetch(url, reqOptions).then((res) => {
      if (res.status === 401) {
        // alert('Unauthorized. Either your session has expired or you are not authorized to perform this action. Please try logging out and logging in again.')
        // if (confirm('Unauthorized. Either your session has expired or you are not authorized to perform this action. Redirect to login page? \n(Please note: if your session has expired, none of the changes you make to this syllabus will be saved until you log in again.)')) {
        //   sylContainer.props.history.push('/log_in');
        // }
        alert(
          "Not authorized to update the syllabus. Please reload the page and try again."
        );
      } else if (res.status >= 400) {
        alert(
          "There was an issue updating the syllabus. Please reload the page and try again."
        );
      } else if (next) {
        next(res);
      }
    });
  } catch (err) {
    //To Do: handle error
  }
};

export default StatefulRichTextList;
